import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { STORAGE_KEYS } from '@/lib/constants';

const executeSchema = z.object({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato: YYYY-MM-DD'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato: YYYY-MM-DD').optional(),
});

type ExecuteFormData = z.infer<typeof executeSchema>;

interface ExtractionExecutorProps {
  onExecute: (data: ExecuteFormData) => Promise<unknown>;
  isLoading?: boolean;
}

type PeriodPreset = 'last_7_days' | 'last_week' | 'last_month';

interface StoredExtractionRange {
  startDate: string;
  endDate: string;
  savedAt: string;
}

function formatDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateLabel(date: string): string {
  const [year, month, day] = date.split('-');
  if (!year || !month || !day) return date;
  return `${day}/${month}/${year}`;
}

function isValidDateString(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function getTodayRange(): { startDate: string; endDate: string } {
  const today = formatDateInput(new Date());
  return { startDate: today, endDate: today };
}

function getPresetRange(preset: PeriodPreset): { startDate: string; endDate: string } {
  const today = new Date();

  if (preset === 'last_7_days') {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return {
      startDate: formatDateInput(start),
      endDate: formatDateInput(today),
    };
  }

  if (preset === 'last_week') {
    // Monday-based calendar week
    const currentWeekday = (today.getDay() + 6) % 7;
    const currentMonday = new Date(today);
    currentMonday.setDate(currentMonday.getDate() - currentWeekday);

    const previousMonday = new Date(currentMonday);
    previousMonday.setDate(previousMonday.getDate() - 7);

    const previousSunday = new Date(currentMonday);
    previousSunday.setDate(previousSunday.getDate() - 1);

    return {
      startDate: formatDateInput(previousMonday),
      endDate: formatDateInput(previousSunday),
    };
  }

  const firstDayCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstDayLastMonth = new Date(
    firstDayCurrentMonth.getFullYear(),
    firstDayCurrentMonth.getMonth() - 1,
    1,
  );
  const lastDayLastMonth = new Date(
    firstDayCurrentMonth.getFullYear(),
    firstDayCurrentMonth.getMonth(),
    0,
  );

  return {
    startDate: formatDateInput(firstDayLastMonth),
    endDate: formatDateInput(lastDayLastMonth),
  };
}

function loadStoredRange(): StoredExtractionRange | null {
  const rawValue = localStorage.getItem(STORAGE_KEYS.KNOWLEDGE_EXTRACTION_LAST_RANGE);
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue) as Partial<StoredExtractionRange>;
    if (!isValidDateString(parsed.startDate) || !isValidDateString(parsed.endDate)) {
      localStorage.removeItem(STORAGE_KEYS.KNOWLEDGE_EXTRACTION_LAST_RANGE);
      return null;
    }

    return {
      startDate: parsed.startDate,
      endDate: parsed.endDate,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : new Date().toISOString(),
    };
  } catch {
    localStorage.removeItem(STORAGE_KEYS.KNOWLEDGE_EXTRACTION_LAST_RANGE);
    return null;
  }
}

function saveStoredRange(range: { startDate: string; endDate: string }) {
  const payload: StoredExtractionRange = {
    startDate: range.startDate,
    endDate: range.endDate,
    savedAt: new Date().toISOString(),
  };

  localStorage.setItem(STORAGE_KEYS.KNOWLEDGE_EXTRACTION_LAST_RANGE, JSON.stringify(payload));
}

export function ExtractionExecutor({ onExecute, isLoading }: ExtractionExecutorProps) {
  const initialRange = loadStoredRange() || getTodayRange();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ExecuteFormData>({
    resolver: zodResolver(executeSchema),
    defaultValues: {
      startDate: initialRange.startDate,
      endDate: initialRange.endDate,
    },
  });

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const handleApplyPreset = (preset: PeriodPreset) => {
    const range = getPresetRange(preset);
    setValue('startDate', range.startDate, { shouldValidate: true, shouldDirty: true });
    setValue('endDate', range.endDate, { shouldValidate: true, shouldDirty: true });
  };

  const handleExecute = async (data: ExecuteFormData) => {
    try {
      await onExecute(data);
      saveStoredRange({
        startDate: data.startDate,
        endDate: data.endDate || data.startDate,
      });
    } catch {
      // Toast de erro ja e exibido no mutation hook.
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Executar Extração Manual</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(handleExecute)} className="space-y-4">
          <div className="space-y-3">
            <Label>Preenchimento Rapido</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => handleApplyPreset('last_7_days')}
              >
                Ultimos 7 dias
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => handleApplyPreset('last_week')}
              >
                Ultima semana
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => handleApplyPreset('last_month')}
              >
                Ultimo mes
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Ultimo periodo usado: {formatDateLabel(startDate)} - {formatDateLabel(endDate || startDate)}
            </p>
          </div>

          <div>
            <Label htmlFor="startDate">Data Inicial</Label>
            <Input
              id="startDate"
              type="date"
              {...register('startDate')}
            />
            {errors.startDate && (
              <p className="text-sm text-red-500 mt-1">{errors.startDate.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="endDate">Data Final (opcional)</Label>
            <Input
              id="endDate"
              type="date"
              {...register('endDate')}
            />
            {errors.endDate && (
              <p className="text-sm text-red-500 mt-1">{errors.endDate.message}</p>
            )}
          </div>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Executando...' : 'Executar Agora'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
